import React, {MutableRefObject, useEffect, useState} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import _ from "lodash";

interface LlmRunCompareDownloadSettings {
  summary: {[key: string]: boolean};
  inputs: {[key: string]: boolean};
  outputs: {[key: string]: boolean};
  results: {[key: string]: boolean};
}

export interface DownloadDialogProps {
  summaryKeys: string[];
  inputKeys: string[];
  outputKeys: string[];
  resultsKeys: string[];
  showSummary: boolean;
  shownInputKeys: string[];
  shownOutputKeys: string[];
  shownResultsKeys: string[];
  open: boolean;
  onClose: () => void;
  tableRef: MutableRefObject<HTMLTableElement>;
}

export function LlmRunCompareDownloadDialog({
  summaryKeys,
  inputKeys,
  outputKeys,
  resultsKeys,
  showSummary,
  shownInputKeys,
  shownOutputKeys,
  shownResultsKeys,
  open,
  onClose,
  tableRef,
}: DownloadDialogProps) {
  const handleDownload = () => {
    downloadCSV(tableRef, state, downloadLandscape);
  };

  const [state, setState] = useState<LlmRunCompareDownloadSettings>({
    summary: Object.fromEntries(summaryKeys.map(key => [key, showSummary])),
    inputs: Object.fromEntries(
      inputKeys.map(key => [key, shownInputKeys.includes(key)]),
    ),
    outputs: Object.fromEntries(
      outputKeys.map(key => [key, shownOutputKeys.includes(key)]),
    ),
    results: Object.fromEntries(
      resultsKeys.map(key => [key, shownResultsKeys.includes(key)]),
    ),
  });

  const [downloadLandscape, setDownloadLandscape] = useState(false);

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      summary: Object.fromEntries(summaryKeys.map(key => [key, showSummary])),
    }));
  }, [showSummary]);

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      inputs: Object.fromEntries(
        inputKeys.map(key => [key, shownInputKeys.includes(key)]),
      ),
    }));
  }, [shownInputKeys]);

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      outputs: Object.fromEntries(
        outputKeys.map(key => [key, shownOutputKeys.includes(key)]),
      ),
    }));
  }, [shownOutputKeys]);

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      results: Object.fromEntries(
        resultsKeys.map(key => [key, shownResultsKeys.includes(key)]),
      ),
    }));
  }, [shownResultsKeys]);

  const handleCheckboxChange = event => {
    const {name, checked} = event.target;

    if (name.includes(".")) {
      const [mainKey, nestedKey] = name.split(".");
      setState(prevState => ({
        ...prevState,
        [mainKey]: {
          ...prevState[mainKey],
          [nestedKey]: checked,
        },
      }));
    } else {
      setState(prevState => ({
        ...prevState,
        [name]: checked,
      }));
    }
  };

  const handleParentCheckboxChange = (event, key) => {
    const checked = event.target.checked;

    setState(prevState => {
      const next = {
        ...prevState,
        [key]: _.mapValues(prevState[key], () => checked),
      };
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Download Options</DialogTitle>
      <DialogContent>
        <p>Select which rows to include in the downloaded CSV file.</p>
        <FormControl component="fieldset">
          <FormLabel component="legend">Table orientation</FormLabel>
          <RadioGroup
            aria-label="orientation"
            name="orientation"
            value={downloadLandscape ? "landscape" : "vertical"}
            onChange={() => setDownloadLandscape(!downloadLandscape)}
          >
            <FormControlLabel
              value="vertical"
              control={<Radio />}
              label="Vertical (default)"
            />
            <FormControlLabel
              value="landscape"
              control={<Radio />}
              label="Landscape"
            />
          </RadioGroup>
        </FormControl>
        {Object.entries(state).map(([dataKey, dataKeyValues]) => {
          if (Object.values(dataKeyValues).length === 0) {
            return <></>;
          }
          return (
            <div key={`checkbox-${dataKey}`}>
              <FormControlLabel
                label={dataKey}
                control={
                  <Checkbox
                    checked={Object.values(state[dataKey]).every(
                      value => value,
                    )}
                    indeterminate={
                      Object.values(state[dataKey]).some(value => value) &&
                      !Object.values(state[dataKey]).every(value => value)
                    }
                    onChange={event =>
                      handleParentCheckboxChange(event, dataKey)
                    }
                    color="primary"
                  />
                }
              />
              <div style={{paddingLeft: "2em"}}>
                {Object.keys(state[dataKey]).map(nestedKey => (
                  <div key={`checkbox-${dataKey}.${nestedKey}`}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state[dataKey][nestedKey]}
                          onChange={handleCheckboxChange}
                          name={`${dataKey}.${nestedKey}`}
                          color="primary"
                        />
                      }
                      label={`${nestedKey}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleDownload} color="primary">
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function downloadCSV(
  tableRef: MutableRefObject<HTMLTableElement>,
  settings: LlmRunCompareDownloadSettings,
  transpose: boolean,
) {
  const includedRowIdsBySection = Object.fromEntries(
    Object.entries(settings).map(([sectionKey, section]) => [
      sectionKey,
      Object.keys(section).filter(rowKey => section[rowKey]),
    ]),
  );

  const includedRowIds = Object.entries(includedRowIdsBySection).reduce(
    (rowIds, [sectionKey, sectionRows]) => {
      if (sectionRows.length > 0) {
        return [
          ...rowIds,
          sectionKey,
          ...sectionRows.map(key => `${sectionKey}.${key}`),
        ];
      }
      return rowIds;
    },
    ["header"],
  );

  const table = tableRef.current;
  let rows = Array.from(table.rows).reduce((rows, row) => {
    if (!includedRowIds.includes(row.id)) {
      return rows;
    }
    return [...rows, Array.from(row.cells)];
  }, []);

  if (transpose) {
    rows = _.zip(...rows);
  }

  const csv = rows.map(rowCells => cellsToCSVRow(rowCells));

  const csvData = csv.join("\n");

  const blob = new Blob([csvData], {type: "text/csv"});

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "llm_run_compare.csv";
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
}

function cellsToCSVRow(cells: HTMLTableCellElement[]) {
  return cells
    .map(cell => `"${(cell.textContent ?? "").replace(/"/g, '""')}"`)
    .join(",");
}
